const normalizeSelection = (selection) => ({
  title: selection.title || '',
  audio: Boolean(selection.audio),
  video: Boolean(selection.video),
  youtube: Boolean(selection.youtube),
  appleVideo: Boolean(selection.appleVideo),
  youtubeAvailable: Boolean(selection.youtubeAvailable),
  appleSelectable: Boolean(selection.appleSelectable),
  appleMaintenance: Boolean(selection.appleMaintenance)
});

const destinationConnectionGuidance = (selection) => {
  const state = normalizeSelection(selection);
  const videoReadyCount = [state.youtubeAvailable, state.appleSelectable]
    .filter(Boolean).length;

  if (videoReadyCount === 0) {
    return {
      video: 'Go to Integrations to connect to desired video platform',
      youtube: null,
      appleVideo: state.appleMaintenance
        ? 'Upload to Apple Podcasts (temporarily unavailable)'
        : null
    };
  }

  if (videoReadyCount === 1) {
    return {
      video: null,
      youtube: state.youtubeAvailable ? null : 'Go to Integrations to connect YouTube',
      appleVideo: state.appleSelectable
        ? null
        : (state.appleMaintenance
          ? 'Upload to Apple Podcasts (temporarily unavailable)'
          : 'Go to Integrations to connect Apple Podcasts')
    };
  }

  return { video: null, youtube: null, appleVideo: null };
};

const destinationSelectionState = (selection) => {
  const state = normalizeSelection(selection);
  const videoDisabled = !state.youtubeAvailable && !state.appleSelectable;
  const hasVideoDestination = state.youtube || state.appleVideo;
  let error = null;

  if (state.video && !hasVideoDestination) error = 'video_destination_required';
  if (!state.audio && !state.video) error ||= 'media_required';
  if (!state.title.trim()) error ||= 'title_required';

  return { ...state, videoDisabled, canContinue: !error, error };
};

const enableVideo = (selection) => destinationSelectionState({
  ...selection,
  video: true,
  youtube: Boolean(selection.youtubeAvailable),
  appleVideo: Boolean(selection.appleSelectable)
});

const toggleVideoDestination = (selection, destination, selected) => {
  const next = { ...selection, [destination]: selected };
  if (selected) next.video = true;
  if (!next.youtube && !next.appleVideo) next.video = false;
  return destinationSelectionState(next);
};

const submittedDestinationValues = (selection) => {
  const state = normalizeSelection(selection);

  return {
    audio: state.audio ? 'true' : 'false',
    video: state.video ? 'true' : 'false',
    youtube: state.youtube ? 'true' : 'false',
    appleVideo: state.appleVideo ? 'true' : 'false'
  };
};

const availableDestinationActions = ({
  youtubeAvailable,
  appleSelectable,
  hasYoutube,
  hasApple
}) => {
  const actions = [];
  if (youtubeAvailable && !hasYoutube) actions.push('youtube');
  if (appleSelectable && !hasApple) actions.push('appleVideo');
  return actions;
};

const initializeDestinationGuidanceTooltips = (root) => {
  if (!root || typeof window === 'undefined' || !window.tippy) return;

  root.querySelectorAll('[data-tippy-content]').forEach((element) => {
    if (element._tippy) return;

    window.tippy(element, {
      theme: 'quill',
      placement: 'top',
      maxWidth: 360,
      offset: [0, 12]
    });
  });
};

const initializeDestinationSelector = (root) => {
  if (!root) return;

  initializeDestinationGuidanceTooltips(root);
  if (root.dataset.destinationSelectorInitialized === 'true') return;

  const form = root.closest('form');
  const title = root.querySelector('[data-title-control]');
  const audio = root.querySelector('[data-audio-control]');
  const video = root.querySelector('[data-video-control]');
  const youtube = root.querySelector('[data-video-destination="youtube"]');
  const apple = root.querySelector('[data-apple-video-control]');
  const submit = form?.querySelector('[data-continue-control]');
  const error = root.querySelector('[data-selection-error]');
  const submittedControls = {
    audio: form?.querySelector('[data-submitted-audio-control]'),
    video: form?.querySelector('[data-submitted-video-control]'),
    youtube: form?.querySelector('[data-submitted-youtube-control]'),
    appleVideo: form?.querySelector('[data-submitted-apple-video-control]')
  };
  if (!form || !title || !audio || !video || !submit) return;

  root.dataset.destinationSelectorInitialized = 'true';

  const currentSelection = () => ({
    title: title.value,
    audio: audio.checked,
    video: video.checked,
    youtube: Boolean(youtube?.checked),
    appleVideo: Boolean(apple?.checked),
    youtubeAvailable: Boolean(youtube && !youtube.disabled),
    appleSelectable: Boolean(apple && !apple.disabled)
  });

  const renderToggle = (control) => {
    const container = control?.closest('[data-toggle-container]');
    const track = container?.querySelector('[data-toggle-track]');
    const thumb = container?.querySelector('[data-toggle-thumb]');
    if (!container || !track || !thumb) return;

    track.classList.toggle('bg-green-600', control.checked);
    track.classList.toggle('bg-gray-200', !control.checked);
    thumb.classList.toggle('translate-x-5', control.checked);
    thumb.classList.toggle('translate-x-0', !control.checked);
    container.classList.toggle('cursor-not-allowed', control.disabled);
    container.classList.toggle('opacity-60', control.disabled);
    container.classList.toggle('cursor-pointer', !control.disabled);
  };

  const syncSubmittedValues = (state) => {
    const values = submittedDestinationValues(state);
    Object.entries(submittedControls).forEach(([name, control]) => {
      if (control) control.value = values[name];
    });
  };

  const render = () => {
    const state = destinationSelectionState(currentSelection());
    video.disabled = state.videoDisabled;
    submit.disabled = !state.canContinue;
    submit.classList.toggle('opacity-50', !state.canContinue);
    submit.classList.toggle('cursor-not-allowed', !state.canContinue);
    submit.classList.toggle('cursor-pointer', state.canContinue);
    submit.classList.toggle('bg-gray-500', !state.canContinue);
    submit.classList.toggle('bg-quill-active', state.canContinue);
    [audio, video, youtube, apple].filter(Boolean).forEach(renderToggle);
    syncSubmittedValues(state);
    if (error) {
      error.textContent = state.error === 'video_destination_required'
        ? 'Select at least one video destination.'
        : '';
    }
  };

  video.addEventListener('change', () => {
    if (video.checked) {
      if (youtube && !youtube.disabled) youtube.checked = true;
      if (apple && !apple.disabled) apple.checked = true;
    } else {
      if (youtube) youtube.checked = false;
      if (apple) apple.checked = false;
    }
    render();
  });

  [youtube, apple].filter(Boolean).forEach((destination) => {
    destination.addEventListener('change', () => {
      if (destination.checked) video.checked = true;
      if (!youtube?.checked && !apple?.checked) video.checked = false;
      render();
    });
  });

  title.addEventListener('input', render);
  audio.addEventListener('change', render);
  form.addEventListener('submit', () => syncSubmittedValues(destinationSelectionState(currentSelection())));
  render();
};

if (typeof document !== 'undefined') {
  document.addEventListener('turbo:load', () => {
    document.querySelectorAll('[data-new-episode-destinations]')
      .forEach(initializeDestinationSelector);
  });
}

module.exports = {
  destinationConnectionGuidance,
  destinationSelectionState,
  enableVideo,
  initializeDestinationGuidanceTooltips,
  toggleVideoDestination,
  submittedDestinationValues,
  availableDestinationActions,
  initializeDestinationSelector
};
